import { Footer, Button } from '@appkit4/react-components';
import React, { useEffect, useState } from 'react';
import classNames from 'classnames';

import '../../styles/pages/LoginFractional.scss';
import '../../styles/pages/Login.scss';
import Form from './LoginForm';
import { useNavigate } from 'react-router-dom';
import { config } from '../../config/env.config';
import StorageService from '../../services/StorageService';
import { generateCodeChallenge, generateCodeVerifier } from '../../utils/pkce';
import axios from 'axios';

const REDIRECT = config.api.redirect;
const AUTH_API = config.api.auth;
const { entraClientId, entraAuthorizeUrl } = config.auth;

const PROD_URL = 'https://documentoperationstoolbox.internal.pwc.it/login';
const IS_TEST_ENV = process.env.REACT_APP_ENVIRONMENT === 'Test';

export interface LanguageText {
    [key: string]: {
        name: string,
        value: {
            title: string,
            name: string,
            ssoLoginText: string,
            footerContent: string,
            footerLinks: string[]
        }
    }
}

export interface LoginProps {
    className?: string,
    type?: 'default' | 'fractional' | 'generic',
    footerContent?: string,
    footerType?: string,
    footerLinks?: ({
        name: string;
        href: string;
        target: string;
    } | {
        name: string;
        href: string;
        target?: undefined;
    })[],
    renderFooterItem?: (item: { name: string, href: string }, index: number) => React.ReactNode,
    textLanguages?: LanguageText,
    backgroundImage?: string,
    renderLogin?: React.ReactNode,
    children?: React.ReactNode[] | React.ReactNode,
    currentIndex?: number,
    backgroundStyle?: React.CSSProperties,
    darkFontColorFractional?: boolean,
    language?: string
}

export const FooterComponent = (textLanguages: LanguageText, languageInfo: string, footerType: string, footerLinks: {name: string, href: string, target?: string}[], renderFooterItem?: ((item: any, index: number) => React.ReactNode) | undefined) => {
    return (
        <Footer content={(textLanguages as LanguageText)[languageInfo].value.footerContent} type={footerType} links={footerLinks.map((item, index: number) => { return { ...item, name: (textLanguages as LanguageText)[languageInfo].value.footerLinks[index] } })} renderFooterItem={renderFooterItem}></Footer>
    );
}

const LoginPattern = React.forwardRef<HTMLElement, LoginProps>(
    (props: LoginProps, ref) => {
        const {
            className,
            type = 'default',
            footerContent = "© 2017 - 2022 PwC. All rights reserved.",
            footerType = 'links',
            footerLinks = [
                { name: 'Privacy policy', href: '#', target: '_blank' },
                { name: 'Cookie notice', href: '#' },
                { name: 'Terms and conditions', href: '#', target: '_self' },
                { name: 'Customize cookie settings', href: '#', target: '_self' }
            ],
            renderFooterItem = (item: { name: string, href: string, target: string }, index: number) => (
                (<div className="ap-footer-link" key={index}>
                    <a className="ap-link" href={item.href} target={item.target ? item.target : '_blank'} rel="noreferrer">{item.name}</a>
                    <span className="ap-footer-divider">|</span>
                </div>)
            ),
            textLanguages = {
                en: {
                    name: 'English',
                    value: {
                        title: 'Welcome to',
                        detail: 'It’s nice to have you back.',
                        txtSSO: 'SSO Login',
                        footerContent,
                        footerLinks: footerLinks.map(item => item.name)
                    }
                }
            },
            backgroundImage,
            currentIndex = 0,
            darkFontColorFractional = false,
            language = 'en'
        } = props;
        let {
            children,
            renderLogin,
            backgroundStyle
        } = props;
        const [currentIndexInfo, setCurrentIndexInfo] = useState(currentIndex);
        const [languageInfo, setLanguageInfo] = useState('en');
        let languages: { label: string, value: string }[] = [];
        for (var key in textLanguages) {
            if (textLanguages.hasOwnProperty(key)) {
                languages.push({ label: (textLanguages as LanguageText)[key].name, value: key });
            }
        }
        React.useEffect(() => {
            setCurrentIndexInfo(currentIndex);
        }, [currentIndex])
        React.useEffect(() => {
            setLanguageInfo(language);
        }, [language])

        const navigate = useNavigate();

        useEffect(() => {
        const token = StorageService.getToken();
        if (!token) return;

        let cancelled = false;

        (async () => {
            try {
            const resp = await axios.get(`${AUTH_API}/authorization`, {
                headers: { 'pwc-identity-token': token }
            });

            if (!cancelled && resp.status === 200) {
                navigate('/');
            }
            } catch (error) {
            if (!cancelled && axios.isAxiosError(error) && error.response?.status === 498) {
                // Token expired, try refresh
                const refreshToken = StorageService.getRefreshToken();
                if (refreshToken) {
                try {
                    const refreshResp = await axios.get(`${AUTH_API}/refresh-token`, {
                    headers: { 'pwc-identity-refresh-token': refreshToken }
                    });

                    if (refreshResp.data?.access_token) {
                    StorageService.setToken(refreshResp.data.access_token);
                    if (refreshResp.data.refresh_token) {
                        StorageService.setRefreshToken(refreshResp.data.refresh_token);
                    }
                    navigate('/');
                    return;
                    }
                } catch {}
                }
            }

            // If refresh failed or other error, clear and stay on login
            if (!cancelled) {
                StorageService.clearAuth();
            }
            }
        })();

        return () => { cancelled = true; };
        }, [navigate]);

        backgroundStyle = backgroundImage? { ...backgroundStyle, backgroundImage: `url(${backgroundImage})` } : backgroundStyle;
        return (
            <div className={classNames('ap-pattern-login', className, {
                'fractional': type === 'fractional',
                'dark-font-color': darkFontColorFractional
            })}>
                {IS_TEST_ENV && (
                    <div className="env-top-banner">
                        ⚠️ Stai visualizzando l'ambiente di test. Siamo in produzione! 🚀{' '}
                        <a href={PROD_URL} target="_blank" rel="noopener noreferrer" className="env-banner-link">
                            Accedi a produzione
                        </a>
                    </div>
                )}
                <div className='login-left-side'>
                    <div className='login-header'>
                        {/* PLACEHOLDER: sostituire con il file SVG del logo PwC "old style" in src/assets/svg/pwc-logo-old.svg */}
                        <div className='login-logo' aria-label="PwC logo" />
                    </div>
                    {currentIndexInfo > 0 ? (Array.isArray(children) ? children[currentIndexInfo - 1] : children) : renderLogin}
                    <div className='login-footer'>
                        {FooterComponent(textLanguages as LanguageText, languageInfo, footerType, footerLinks, renderFooterItem)}
                    </div>
                </div>
                <div className='login-right-side'>
                    {type !== 'fractional' && <div className='ap-pattern-login-wrapper' style={backgroundStyle}></div>}
                </div>
                {type === 'fractional' && <div className='ap-pattern-login-wrapper' style={backgroundStyle}></div>}
            </div>
        );
})

export default function LoginFractional() {
    const textLanguages = React.useMemo<LanguageText>(() => ({
        en: {
            name: 'Italiano',
            value: {
                title: 'Benevenuti su:',
                name: 'Document Operations Toolbox',
                ssoLoginText: 'SSO Login',
                footerContent: "© 2026 PwC. Tutti i diritti riservati. PwC si riferisce alla società membro italiana della rete PwC o a una delle sue filiali o affiliate.",
                footerLinks: ['Privacy policy', 'Cookie notice', 'Termini e condizioni', 'Impostazioni cookie']
            }
        }
    }), []);

    const [showModal, setShowModal] = React.useState(false);

    const handleSSOClick = () => {
        if (IS_TEST_ENV) {
            setShowModal(true);
        } else {
            onSSOLogin();
        }
    };

    const onSSOLogin = async () => {
        const codeVerifier = generateCodeVerifier();
        StorageService.setCodeVerifier(codeVerifier);
        const codeChallenge = await generateCodeChallenge(codeVerifier);

        const state = generateCodeVerifier();
        StorageService.setOAuthState(state);

        const authUrl = new URL(entraAuthorizeUrl);
        authUrl.searchParams.set('client_id', entraClientId);
        authUrl.searchParams.set('response_type', 'code');
        authUrl.searchParams.set('redirect_uri', `${REDIRECT}/redirect`);
        authUrl.searchParams.set('response_mode', 'query');
        authUrl.searchParams.set('scope', 'openid profile email offline_access');
        authUrl.searchParams.set('state', state);
        authUrl.searchParams.set('code_challenge', codeChallenge);
        authUrl.searchParams.set('code_challenge_method', 'S256');

        window.location.href = authUrl.toString();
    };

    const labels = textLanguages['en'].value;

    return (
        <>
            <LoginPattern className="fractional" type="fractional" language="en" textLanguages={textLanguages}
                renderLogin={
                    <Form
                        solid
                        onCancel={() => undefined}
                        onOk={handleSSOClick}
                        headerLayout="vertical"
                        footerLayout="vertical"
                        footer={
                            <Button kind='primary' onClick={handleSSOClick}>
                                {labels.ssoLoginText}
                            </Button>
                        }
                        header={
                            <>
                                {IS_TEST_ENV && (
                                    <div className="env-inner-banner">
                                        ⚠️ Stai visualizzando l'ambiente di test. Siamo in produzione! 🚀{' '}
                                        <a href={PROD_URL} target="_blank" rel="noopener noreferrer" className="env-banner-link">
                                            Accedi a produzione
                                        </a>
                                    </div>
                                )}
                                <span className='ap-pattern-form-login-welcome'>
                                    {labels.title}
                                </span>
                                <div className='ap-pattern-form-login-title'>
                                    {labels.name}
                                </div>
                            </>
                        }
                    />
                }
            >
            </LoginPattern>

            {IS_TEST_ENV && showModal && (
                <div className="env-modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="env-modal" onClick={e => e.stopPropagation()}>
                        <button className="env-modal-close" onClick={() => setShowModal(false)} aria-label="Chiudi">✕</button>
                        <div className="env-modal-title">Login non disponibile</div>
                        <div className="env-modal-body">
                            Stai accedendo all'ambiente di <strong>Test</strong>. Il login è disabilitato su questo ambiente.
                            Per accedere al sistema utilizza l'ambiente di produzione.
                        </div>
                        <a
                            href={PROD_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="env-modal-prod-btn"
                        >
                            🚀 Accedi a produzione
                        </a>
                    </div>
                </div>
            )}
        </>
    );
}
